import React, {useCallback, useMemo, useState} from 'react';
import {Button, Col, Container, Row} from "react-bootstrap";
import {Form, Formik} from "formik";
import {Redirect} from "react-router-dom";
import * as yup from 'yup';
import NavHeader from "../components/Navigation/NavHeader";
import {ChrisUserProviderContext} from "../components/Providers/ChrisUserProvider";
import StandardField, {StandardFieldOption} from "../components/Forms/Fields/StandardField";
import {apiEndpoint} from "../utils/api";
import GeneralErrorModal from "../components/Modal/GeneralErrorModal";
import {phoneValidation} from "../utils/formHelpers";
import {useApiFetch} from "../components/Providers/OidcProvider";

const recordAccessOptions: StandardFieldOption[] = [
    {label: 'View Only', value: 'View Only', checked: false},
    {label: 'Edit', value: 'Edit', checked: false},
    {label: 'Edit/Delete', value: 'Edit/Delete', checked: false},
];

const userTypeOptions: StandardFieldOption[] = [
    {label: 'Data Entry/Staff', value: 'Data Entry/Staff', checked: false},
    {label: 'Data Facilitator', value: 'Data Facilitator', checked: false},
    {label: 'Manager', value: 'Manager', checked: false},
    {label: 'ESE Director', value: 'ESE Director', checked: false},
    {label: 'Other', value: 'Other', checked: false},
];

const schema = yup.object({
    email: yup.string().email('Must be a valid email').required('Required'),
    firstName: yup.string().required('Required'),
    lastName: yup.string().required('Required'),
    fdlrsSite: yup.string().required('Required'),
    phoneNumber: phoneValidation,
    countyAccess: yup.array().of(yup.string()).min(1, 'Required').required('Required'),
    recordAccessType: yup.string().required('Required'),
    userType: yup.string().required('Required'),
    userTypeOther: yup.string().when('userType', {
        is: 'Other',
        then: yup.string().required('Required'),
        otherwise: yup.string(),
    }),
    dataFacilitatorSignature: yup.string().required('Required'),
    comments: yup.string(),
});

type FormData = yup.InferType<typeof schema>;

const initialValues : FormData = {
    email: '',
    firstName: '',
    lastName: '',
    fdlrsSite: '',
    phoneNumber: '',
    countyAccess: [],
    recordAccessType: '',
    userType: '',
    userTypeOther: '',
    dataFacilitatorSignature: '',
    comments: '',
};

const NewUser = () => {
    const [chrisUserState] = React.useContext(ChrisUserProviderContext);
    const [submitted, setSubmitted] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const apiFetch = useApiFetch();
    const [{chrisUser}, siteLists] = React.useContext(ChrisUserProviderContext);

    const residenceCountyList = useMemo(() => {
        return siteLists.find(
            list => (list.name === 'ResidenceServiceCountyList')
        )?.values ?? [];
    }, [siteLists]);

    const handleSetFormIsModified = useCallback(async (_: boolean) => {}, []);

    const handleSubmit = useCallback(async (values: FormData) => {
        const url = new URL('/v1/user/request-new-user', apiEndpoint);

        try {
            const response = await apiFetch(url.toString(), {
                method: 'POST',
                body: JSON.stringify(values)
            });

            if (response.ok) {
                setSubmitted(true);
                return;
            }
        } catch (e) {
            console.error('error requesting user', e);
        }
        setShowErrorModal(true);
    }, []);

    if (!chrisUserState.chrisUser.isDataFacilitator) {
        return <Redirect to="/record-locator" />;
    }

    if (submitted) {
        return (
            <>
                <NavHeader />
                <Container>
                    <Row className="mt-4">
                        <Col xs={12} className="d-flex flex-column align-items-center">
                            <h4>Your request has been submitted.</h4>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <NavHeader />
            <Container>
                <Row className="mt-4">
                    <Col xs={12}>
                        <h4>CHRIS New User Data Form</h4>
                        <p>All new user accounts must be requested by the CHRIS data facilitator. Please fill out this form and click Submit to send it to the CHRIS Help Desk.</p>
                        <p className="text-danger small">* Indicates required question</p>
                    </Col>
                </Row>
                <Formik
                    initialValues={{
                        ...initialValues,
                        countyAccess: [],
                        fdlrsSite: chrisUser.siteName,
                    }}
                    validationSchema={schema}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={async (values) => {
                        await handleSubmit(values);
                    }}
                >
                {(props) => (
                    <Form>
                        <Row className="mx-0">
                            <Col
                                xs={12}
                                className="py-2 px-3 bg-primary text-white"
                            >
                                <strong>User Information</strong>
                            </Col>
                        </Row>
                        <Row className="chris-form-bg mx-0 py-3 px-2">
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="firstName"
                                    type="text"
                                    label={<>New User First Name <span className="text-danger">*</span></>}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="lastName"
                                    type="text"
                                    label={<>New User Last Name <span className="text-danger">*</span></>}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="email"
                                    type="text"
                                    label={<>Email <span className="text-danger">*</span></>}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="phoneNumber"
                                    type="text"
                                    label={<>Phone Number <span className="text-danger">*</span></>}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="fdlrsSite"
                                    type="text"
                                    readOnly={true}
                                    label={<>FDLRS Site Name <span className="text-danger">*</span></>}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                        </Row>
                        <Row className="mx-0 mt-3">
                            <Col
                                xs={12}
                                className="py-2 px-3 bg-primary text-white"
                            >
                                <strong>Record Access Information</strong>
                            </Col>
                        </Row>
                        <Row className="chris-form-bg mx-0 py-3 px-2">
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="countyAccess"
                                    type="checkbox"
                                    label={<>County Access <span className="text-danger">*</span></>}
                                    options={residenceCountyList}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="recordAccessType"
                                    type="radio"
                                    label={<>Type of Record Access <span className="text-danger">*</span></>}
                                    options={recordAccessOptions}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="userType"
                                    type="radio"
                                    label={<>Type of User <span className="text-danger">*</span></>}
                                    options={userTypeOptions}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                                {props.values.userType === 'Other' && (
                                    <StandardField
                                        name="userTypeOther"
                                        type="text"
                                        label={<>Other <span className="text-danger">*</span></>}
                                        handleSetFormIsModified={handleSetFormIsModified}
                                    />
                                )}
                            </Col>
                            <Col xs={12} lg={6}>
                                <StandardField
                                    name="dataFacilitatorSignature"
                                    type="text"
                                    label={<>CHRIS Data Facilitator Signature (Full Name) <span className="text-danger">*</span></>}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                            <Col xs={12}>
                                <StandardField
                                    name="comments"
                                    type="textarea"
                                    label="Comments"
                                    rows={4}
                                    handleSetFormIsModified={handleSetFormIsModified}
                                />
                            </Col>
                        </Row>
                        <Row className="mx-0 mt-3 mb-4">
                            <Col xs={12} className="d-flex justify-content-end">
                                <Button
                                    className="btn-secondary"
                                    variant="primary"
                                    type="submit"
                                    disabled={props.isSubmitting}
                                >
                                    Submit
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                )}
                </Formik>
                <GeneralErrorModal
                    show={showErrorModal}
                    handleShowGeneralErrorModal={setShowErrorModal}
                />
            </Container>
        </>
    );
};

export default NewUser;
